import { $logger } from '@/boot/logger'
import { $pegApi } from '@/boot/peg-api'

/**
 * @typedef {Object} DynamicQuestion
 * @property {string} id
 * @property {string} returnSessionItemId
 * @property {string} questionText
 * @property {'YES_NO'|'MULTIPLE_CHOICE'} questionType
 * @property {Array<{value: string, label: string}>|null} choicesJson
 * @property {string|null} answer
 * @property {string|null} answeredAt
 * @property {boolean} [blocked] Backend-derived: this persisted answer confirmed a non-returnable condition (PEG-786 rehydration signal).
 */

/**
 * Customer Return Dynamic Questions Store (PEG-786 Source 3)
 *
 * Holds the AI-generated dynamic questions for the active return session and
 * the customer's in-progress answers, and submits each answer to the backend.
 * Backend generation/persistence lives in peg-backend (the
 * return_session_dynamic_questions table); this store is the shopper-facing
 * flow-injection layer.
 *
 * Flow:
 *   - loadQuestions() is called when the return flow reaches the
 *     dynamic-questions step (after all items are processed, before summary).
 *   - The customer answers each question (YES_NO or MULTIPLE_CHOICE), grouped
 *     on a single page.
 *   - submitAnswers() POSTs each answer; the backend records answer +
 *     answered_at and, when an answer confirms the non-returnable condition,
 *     blocks the return (logs return_blocked_attempts with
 *     validation_source='AI_DYNAMIC_QUESTION'). If any question blocks, the
 *     flow routes the customer to the blocked page instead of the summary.
 *
 * @public API:
 * - State: questions, answersById, loading, submitting, blocked
 * - Computed: hasQuestions, unansweredQuestions, hasUnansweredQuestions,
 *   allAnswered
 * - Actions: loadQuestions, setAnswer, submitAnswers, reset
 *
 * @store customerReturnDynamicQuestions
 */
export const useCustomerReturnDynamicQuestionsStore = defineStore('customerReturnDynamicQuestions', () => {
  const sessionStore = useCustomerReturnSessionStore()
  const { returnSessionId } = storeToRefs(sessionStore)

  // ==========================================================================
  // STATE
  // ==========================================================================

  /** @type {import('vue').Ref<DynamicQuestion[]>} */
  const questions = ref([])
  /** Local, in-progress answers keyed by question id (string answer value). */
  const answersById = ref({})
  const loading = ref(false)
  const submitting = ref(false)
  /** True once an answer has confirmed a non-returnable condition. */
  const blocked = ref(false)

  // ==========================================================================
  // COMPUTED
  // ==========================================================================

  const hasQuestions = computed(() => questions.value.length > 0)

  /**
   * Single source of truth for "this answer value is real / persisted".
   * Treats null, undefined AND empty string as NOT answered, so every gate
   * (unanswered, unpersisted, blocked rehydration, submit skip) agrees on the
   * empty-string case — otherwise a backend `''` could read as answered in one
   * place and unpersisted in another, enabling Continue while the route guard
   * keeps redirecting (summary ↔ dynamic-questions loop).
   * @param {string|null|undefined} answer
   * @returns {boolean}
   */
  function isAnswered(answer) {
    return answer !== undefined && answer !== null && answer !== ''
  }

  const unansweredQuestions = computed(() =>
    questions.value.filter(q => !isAnswered(answersById.value[q.id]) && !isAnswered(q.answer))
  )

  const hasUnansweredQuestions = computed(() => unansweredQuestions.value.length > 0)

  /**
   * PEG-786: questions whose answer is NOT yet persisted on the backend
   * (`q.answer` is null/empty), regardless of any local in-memory answer.
   * The flow-injection gate uses this — NOT `hasUnansweredQuestions` — so a
   * local answer that was never written (user answered, navigated away/back,
   * or a submit failed) cannot let the customer skip the questions step and
   * reach the summary without persisting. Fails closed: anything not proven
   * persisted re-injects the step.
   */
  const unpersistedQuestions = computed(() => questions.value.filter(q => !isAnswered(q.answer)))

  const hasUnpersistedQuestions = computed(() => unpersistedQuestions.value.length > 0)

  const allAnswered = computed(() => hasQuestions.value && !hasUnansweredQuestions.value)

  // ==========================================================================
  // ACTIONS
  // ==========================================================================

  /**
   * Generate (idempotent on the backend) and load the dynamic questions for
   * the active return session. Pre-seeds answersById with any answers already
   * persisted (so a refresh mid-step keeps prior answers).
   * @returns {Promise<DynamicQuestion[]>}
   */
  async function loadQuestions() {
    if (!returnSessionId.value) {
      $logger.warn('[dynamicQuestions] loadQuestions called with no active return session')
      // Clear ALL session-scoped state, not just questions: a prior session's
      // `answersById`/`blocked` would otherwise leak into the summary guard and
      // navigation injection (which read `blocked`), redirecting to the
      // dynamic-questions step with no active session.
      questions.value = []
      answersById.value = {}
      blocked.value = false
      return []
    }

    loading.value = true
    try {
      // $fetch.peg throws on non-2xx (AGENTS.md API Layer) — the catch below
      // handles failures and fails safe. No response.success check.
      const { questions: fetched } = await $pegApi.ReturnSessionDynamicQuestions.getForSession(returnSessionId.value)

      questions.value = Array.isArray(fetched) ? fetched : []
      // Seed local answers from any already-persisted answers (empty string is
      // NOT persisted — see isAnswered — so it never pre-fills the UI).
      for (const q of questions.value) {
        if (isAnswered(q.answer) && answersById.value[q.id] === undefined) {
          answersById.value[q.id] = q.answer
        }
      }
      // PEG-786: rehydrate `blocked` from persisted state so a page refresh /
      // store re-init can't reset a previously-recorded block to false and let
      // the customer slip past to the summary. `blocked` is only set during
      // submitAnswers(), and submitAnswers() skips already-persisted answers,
      // so without this a refresh after a blocking answer would clear the flag.
      // Backend surfaces the per-question blocking signal as `blocked` (or
      // `isBlocking`); fail closed if any persisted question carries it.
      blocked.value = questions.value.some(q => isAnswered(q.answer) && (q.blocked === true || q.isBlocking === true))
      return questions.value
    } catch (error) {
      $logger.error('[dynamicQuestions] error loading questions', { error })
      // Fail safe: clear ALL session-scoped state so a prior session's stale
      // answersById/blocked can't keep the summary guard / navigation injection
      // redirecting to the dynamic-questions step after a load error.
      questions.value = []
      answersById.value = {}
      blocked.value = false
      return []
    } finally {
      loading.value = false
    }
  }

  /**
   * Set the customer's local answer for a question.
   * @param {string} questionId
   * @param {string} answer
   */
  function setAnswer(questionId, answer) {
    answersById.value = { ...answersById.value, [questionId]: answer }
  }

  /**
   * Submit every answered question to the backend. Records each answer and, if
   * any answer confirms the non-returnable condition, sets `blocked`. Only
   * sends questions that are not already persisted, to avoid redundant writes.
   * @returns {Promise<{success: boolean, blocked: boolean}>}
   */
  async function submitAnswers() {
    submitting.value = true
    blocked.value = false
    try {
      for (const q of questions.value) {
        const localAnswer = answersById.value[q.id]
        // Skip questions with no local answer or already persisted on the server.
        // Empty string counts as neither (see isAnswered), so a backend `''`
        // can still be written rather than perpetually skipped.
        if (!isAnswered(localAnswer)) continue
        if (isAnswered(q.answer)) continue

        // $fetch.peg throws on non-2xx (AGENTS.md API Layer) — the catch below
        // handles failures. No response.success check.
        const { question, blocked: didBlock } = await $pegApi.ReturnSessionDynamicQuestions.answer(q.id, String(localAnswer))

        // Reflect the persisted answer back onto the question.
        if (question) {
          q.answer = question.answer
          q.answeredAt = question.answeredAt
        }

        if (didBlock) {
          blocked.value = true
        }
      }
      return { success: true, blocked: blocked.value }
    } catch (error) {
      $logger.error('[dynamicQuestions] error submitting answers', { error })
      return { success: false, blocked: false }
    } finally {
      submitting.value = false
    }
  }

  /**
   * Reset all dynamic-question state (called when the return flow resets).
   */
  function reset() {
    questions.value = []
    answersById.value = {}
    loading.value = false
    submitting.value = false
    blocked.value = false
  }

  return {
    // State
    questions,
    answersById,
    loading,
    submitting,
    blocked,
    // Computed
    hasQuestions,
    unansweredQuestions,
    hasUnansweredQuestions,
    unpersistedQuestions,
    hasUnpersistedQuestions,
    allAnswered,
    // Actions
    loadQuestions,
    setAnswer,
    submitAnswers,
    reset,
  }
})
